import {
    type FC,
    type ReactNode,
    Suspense,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import classnames from 'classnames';
import { graphql, usePreloadedQuery, useQueryLoader, type PreloadedQuery } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import dibsCss from 'dibs-css';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { BuyerProductTileShimmer } from 'dibs-buyer-product-tile/exports/BuyerProductTileShimmer.ts';
import { TILE_SIZES } from 'dibs-buyer-product-tile/exports/BuyerProductTile.ts';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import {
    UserRecommendationsTitle,
    type UserRecommendationsTitleAlign,
} from './UserRecommendationsTitle.tsx';
import { UserRecommendationsRecommendationTile } from './UserRecommendationsRecommendationTile.tsx';
import { useUserRecommendationsItemCounts } from './useUserRecommendationsItemCounts.ts';
import {
    trackUserRecommendationsViewLessClick,
    trackUserRecommendationsViewMoreClick,
} from './userRecommendationsTracking.ts';

import { type UserRecommendationsGridQuery } from './__generated__/UserRecommendationsGridQuery.graphql.ts';

import styles from './UserRecommendations.module.css';

const DESKTOP_ITEMS_PER_ROW = 5;
const TABLET_ITEMS_PER_ROW = 4;
const MOBILE_ITEMS_PER_ROW = 2;

export const userRecommendationsGridQuery = graphql`
    query UserRecommendationsGridQuery(
        $itemIds: [String]!
        $userId: String!
        $isTrade: Boolean!
        $priceBookName: String!
    ) {
        viewer {
            ...UserRecommendationsRecommendationTile_viewer
                @arguments(fetchFavorites: true, userId: $userId)
            itemRead(itemIds: $itemIds) {
                ... on Item {
                    __typename
                    serviceId
                    ...UserRecommendationsRecommendationTile_item
                        @arguments(
                            fetchFavorites: true
                            userId: $userId
                            isTrade: $isTrade
                            priceBookName: $priceBookName
                        )
                }
            }
            user(userId: $userId) {
                ...UserRecommendationsRecommendationTile_user
            }
        }
    }
`;

type UserRecommendationsLoadedRowsProps = {
    queryRef: PreloadedQuery<UserRecommendationsGridQuery>;
    itemsPerRow: number;
    isMobile: boolean;
    trackingTrigger: string;
    dataTn?: string;
    startIndex: number;
    currency: string;
};

const UserRecommendationsLoadedRows: FC<UserRecommendationsLoadedRowsProps> = ({
    queryRef,
    itemsPerRow,
    isMobile,
    trackingTrigger,
    dataTn,
    startIndex,
    currency,
}) => {
    const data = usePreloadedQuery<UserRecommendationsGridQuery>(
        userRecommendationsGridQuery,
        queryRef
    );
    const items =
        data.viewer?.itemRead?.filter(filterFalsy).filter(item => item.__typename === 'Item') || [];

    if (!items.length) {
        return null;
    }

    return (
        <>
            {items.map((item, index) => (
                <UserRecommendationsRecommendationTile
                    key={item.serviceId}
                    item={item}
                    viewer={data.viewer}
                    user={data.viewer?.user}
                    index={startIndex + index}
                    itemsPerRow={itemsPerRow}
                    isMobile={isMobile}
                    trackingTrigger={trackingTrigger}
                    dataTn={dataTn}
                    currency={currency}
                />
            ))}
        </>
    );
};

const renderShimmers = (count: number, keyPrefix: string): ReactNode[] =>
    Array.from({ length: count }, (_, index) => (
        <div key={`${keyPrefix}-${index}`} className={dibsCss.minW0}>
            <BuyerProductTileShimmer size={TILE_SIZES.medium} />
        </div>
    ));

export type UserRecommendationsItemsGridProps = {
    itemIds: readonly string[];
    userId: string;
    trackingTrigger: string;
    isMobile: boolean;
    isTablet: boolean;
    isTrade: boolean;
    priceBookName: string;
    dataTn?: string;
    title?: ReactNode;
    currency: string;
    titleAlign?: UserRecommendationsTitleAlign;
};

const UserRecommendationsItemsGrid: FC<UserRecommendationsItemsGridProps> = ({
    itemIds: itemIdsProp,
    userId,
    trackingTrigger,
    isMobile,
    isTablet,
    isTrade,
    priceBookName,
    dataTn = 'user-recommendations',
    title,
    currency,
    titleAlign = 'center',
}) => {
    const { initialItemCount, maxItemCount, moreItemCount } =
        useUserRecommendationsItemCounts(isMobile);
    const itemIds = useMemo(() => itemIdsProp.slice(0, maxItemCount), [itemIdsProp, maxItemCount]);
    const [isExpanded, setIsExpanded] = useState(false);
    const moduleRef = useRef<HTMLDivElement>(null);
    const shouldScrollToModuleTopRef = useRef(false);
    const [itemsQueryRef, loadItemsQuery] = useQueryLoader<UserRecommendationsGridQuery>(
        userRecommendationsGridQuery
    );
    const [moreItemsQueryRef, loadMoreItemsQuery] = useQueryLoader<UserRecommendationsGridQuery>(
        userRecommendationsGridQuery
    );

    const initialItemIds = useMemo(
        () => itemIds.slice(0, initialItemCount),
        [itemIds, initialItemCount]
    );

    const moreItemIds = useMemo(() => itemIds.slice(initialItemCount), [itemIds, initialItemCount]);

    useEffect(() => {
        if (!initialItemIds.length || itemsQueryRef) {
            return;
        }
        loadItemsQuery({
            itemIds: [...initialItemIds],
            userId,
            isTrade,
            priceBookName,
        });
    }, [initialItemIds, isTrade, itemsQueryRef, loadItemsQuery, priceBookName, userId]);

    let itemsPerRow = DESKTOP_ITEMS_PER_ROW;
    if (isMobile) {
        itemsPerRow = MOBILE_ITEMS_PER_ROW;
    } else if (isTablet) {
        itemsPerRow = TABLET_ITEMS_PER_ROW;
    }

    const hasMoreToLoad = itemIds.length > initialItemCount;
    const showViewMore = !isExpanded && hasMoreToLoad;
    const showViewLess = isExpanded;
    const showToggleButton = showViewMore || showViewLess;

    const handleToggleExpand = useCallback(() => {
        if (isExpanded) {
            shouldScrollToModuleTopRef.current = true;
            trackUserRecommendationsViewLessClick(trackingTrigger);
            setIsExpanded(false);
            return;
        }
        if (moreItemIds.length && !moreItemsQueryRef) {
            loadMoreItemsQuery({
                itemIds: [...moreItemIds],
                userId,
                isTrade,
                priceBookName,
            });
        }
        trackUserRecommendationsViewMoreClick(trackingTrigger);
        setIsExpanded(true);
    }, [
        isExpanded,
        isTrade,
        loadMoreItemsQuery,
        moreItemIds,
        moreItemsQueryRef,
        priceBookName,
        trackingTrigger,
        userId,
    ]);

    useEffect(() => {
        if (!shouldScrollToModuleTopRef.current || !moduleRef.current) {
            return;
        }
        shouldScrollToModuleTopRef.current = false;
        moduleRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [isExpanded]);

    if (!itemIds.length) {
        return null;
    }

    const titleContent = title ?? (
        <UserRecommendationsTitle titleAlign={titleAlign} isMobile={isMobile} />
    );

    if (!itemsQueryRef) {
        return (
            <div
                ref={moduleRef}
                className={classnames(isMobile ? dibsCss.myLarge : dibsCss.myLarger)}
                data-tn={dataTn}
            >
                {titleContent}
                <div
                    className={classnames(
                        dibsCss.grid,
                        styles.grid,
                        isMobile ? dibsCss.gapSmaller : dibsCss.gapSmall,
                        !isMobile && isTablet && styles.gridTablet,
                        !isMobile && !isTablet && styles.gridDesktop,
                        dibsCss.mtMedium
                    )}
                >
                    {renderShimmers(initialItemCount, 'initial')}
                </div>
            </div>
        );
    }

    return (
        <div
            ref={moduleRef}
            className={classnames(isMobile ? dibsCss.myLarge : dibsCss.myLarger)}
            data-tn={dataTn}
        >
            {titleContent}
            <div
                className={classnames(
                    dibsCss.grid,
                    styles.grid,
                    isMobile
                        ? [dibsCss.gapSmaller, dibsCss.mtSmall]
                        : [dibsCss.gapSmall, dibsCss.mtLarge],
                    !isMobile && isTablet && styles.gridTablet,
                    !isMobile && !isTablet && styles.gridDesktop
                )}
            >
                <Suspense fallback={renderShimmers(initialItemCount, 'initial-loading')}>
                    <UserRecommendationsLoadedRows
                        queryRef={itemsQueryRef}
                        itemsPerRow={itemsPerRow}
                        isMobile={isMobile}
                        trackingTrigger={trackingTrigger}
                        dataTn={dataTn}
                        startIndex={0}
                        currency={currency}
                    />
                </Suspense>
                {isExpanded && moreItemIds.length > 0 && !moreItemsQueryRef
                    ? renderShimmers(moreItemCount, 'more')
                    : null}
                {isExpanded && moreItemsQueryRef ? (
                    <Suspense fallback={renderShimmers(moreItemCount, 'more-loading')}>
                        <UserRecommendationsLoadedRows
                            queryRef={moreItemsQueryRef}
                            itemsPerRow={itemsPerRow}
                            isMobile={isMobile}
                            trackingTrigger={trackingTrigger}
                            dataTn={dataTn}
                            startIndex={initialItemCount}
                            currency={currency}
                        />
                    </Suspense>
                ) : null}
            </div>
            {showToggleButton && (
                <div className={classnames(dibsCss.flex, dibsCss.justifyCenter, dibsCss.mtSmall)}>
                    <Button
                        type="secondary"
                        size="medium"
                        dataTn={`${dataTn}-toggle`}
                        onClick={handleToggleExpand}
                    >
                        {showViewLess ? (
                            <FormattedMessage
                                id="UserRecommendations.viewFewer"
                                defaultMessage="View Fewer Items"
                            />
                        ) : (
                            <FormattedMessage
                                id="UserRecommendations.viewMore"
                                defaultMessage="View More Items"
                            />
                        )}
                    </Button>
                </div>
            )}
        </div>
    );
};

export default UserRecommendationsItemsGrid;
